import { Component, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ActivatedRoute, Router } from '@angular/router';
import { Table } from 'primeng/table';
import { MessageService } from 'primeng/api';
import { bmxurl, ConvertDateToDB } from 'src/globals';
import { booking_returndata } from 'src/types/booking_returndata';
import { BusyService } from '../_services/busy.service';
import { GlobalService } from '../_services/global.service';

//The Find button on At a Glance lands here: every booking on the date the toolbar was showing,
//filterable and sortable, with the same admit and leave the scan dialog offers.
@Component({
  selector: 'app-ataglancebookings',
  templateUrl: './ataglancebookings.component.html',
  styleUrls: ['./ataglancebookings.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class AtaglancebookingsComponent implements OnInit {

  @ViewChild('resourceTable') resourceTable: Table;

  ListBookings: any[] = [];
  VenueID: string = localStorage.getItem("SmartVenueID")!;
  CurrencySymbol: string = localStorage.getItem("Currency") == null ? "GBP" : localStorage.getItem("Currency")!;

  BookingDate: Date = new Date();
  startdate: string = "";

  //Admitting is only meaningful on the day itself, so the buttons are shown but inert on any
  //other date rather than hidden - the person on the counter can still see who is in.
  get isBookingDateToday(): boolean {
    if (!this.BookingDate) return false;

    const today = new Date();

    return this.BookingDate.getFullYear() === today.getFullYear()
      && this.BookingDate.getMonth() === today.getMonth()
      && this.BookingDate.getDate() === today.getDate();
  }

  constructor(
    private route: ActivatedRoute,
    private http: HttpClient,
    private router: Router,
    private messageService: MessageService,
    private busyService: BusyService,
    private globalService: GlobalService) {
  }

  ngOnInit(): void {
    this.globalService.setSmallScreenMenu(false);

    this.startdate = this.route.snapshot.paramMap.get('startdate');

    if (this.startdate != null && this.startdate != "" && this.startdate != "null") {
      this.BookingDate = this.parseDate(this.startdate);
    }

    this.startdate = this.stringifyDate(this.BookingDate);

    this.loadgrid();
  }

  //The date arrives as YYYY-MM-DD. Read part by part so it lands on local midnight rather than
  //UTC midnight, which can be the day before.
  private parseDate(dateIn: string): Date {
    const parts = dateIn.substring(0, 10).split('-');

    if (parts.length == 3) {
      const parsed = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
      if (!isNaN(parsed.getTime())) { return parsed; }
    }

    const fallback = new Date(dateIn);

    return isNaN(fallback.getTime()) ? new Date() : fallback;
  }

  stringifyDate(date: Date): string {
    return date.getFullYear()
      + "-" + ('00' + (Number(date.getMonth()) + 1)).slice(-2)
      + "-" + ('00' + date.getDate()).slice(-2);
  }

  //The same call the Booking List makes, with the start and end pinned to the one date.
  loadgrid(): void {
    let searchCrit = {
      venueid: this.VenueID,
      startdate: ConvertDateToDB(this.BookingDate),
      enddate: ConvertDateToDB(this.BookingDate),
      tags: [],
      disabled: false,
      created: true
    }

    this.busyService.busy();

    this.http.post(bmxurl + "/BookingList", searchCrit).subscribe(response => {
      try {
        let result = (response as booking_returndata);

        let bookings: any[] = (result.data as any[]) ?? [];

        //A booking still being taken at another till is not one anybody can be admitted on.
        this.ListBookings = bookings.filter(booking => booking.inProgress == false);
      }
      catch (error) {
        this.messageService.add({ severity: 'error', summary: 'Error getting bookings', detail: 'Failed to get the bookings for this date.' });
      }
      finally {
        this.busyService.idle();
      }
    }, error => {
      this.messageService.add({ severity: 'error', summary: 'Error getting bookings', detail: 'An error occurred getting Bookings' });
      this.busyService.idle();
    });
  }

  editBooking(bookingId: string): void {
    //So Cancel on the booking comes back to this list, on the date it was opened from.
    localStorage.setItem("aagBookingList", this.startdate);
    this.router.navigateByUrl("/editbooking/" + bookingId);
  }

  goBack(): void {
    this.router.navigateByUrl("/ataglance/" + this.startdate);
  }

  maskMobile(mobileIn: string): string {
    if (mobileIn != null && mobileIn.length > 4) {
      return "**** **** " + mobileIn.slice(-4);
    }

    return mobileIn || "";
  }

  admitall(data: any): void {
    if (!this.isBookingDateToday) { return; }

    //A booking with a waiver still to sign is sent to the waiver screen rather than admitted
    //from here, the same as the scan dialog does.
    if (data.haswaiver) {
      localStorage.setItem("aagBookingList", this.startdate);
      this.router.navigate(['/managewaivers', data.bookingId], {
        queryParams: { returnDate: data.startTime }
      });
      return;
    }

    this.http.get(bmxurl + "BookingCheckin/" + this.VenueID + "/" + data.bookingId).subscribe(response => {
      let result = response as any;

      if (result.succeeded) {
        this.messageService.add({ severity: 'success', summary: 'Admitted', detail: "All customers have been marked as 'admitted' for this booking." });
        this.updategrid(data.bookingId, true);
      }
      else {
        this.messageService.add({ severity: 'error', summary: 'Not Admitted', detail: 'An error occurred admitting all customers for this booking.' });
      }
    }, () => {
      this.messageService.add({ severity: 'error', summary: 'Not Admitted', detail: 'An error occurred admitting all customers for this booking.' });
    });
  }

  leaveall(data: any): void {
    if (!this.isBookingDateToday) { return; }

    this.http.get(bmxurl + "BookingCheckout/" + this.VenueID + "/" + data.bookingId).subscribe(response => {
      let result = response as any;

      if (result.succeeded) {
        this.messageService.add({ severity: 'success', summary: 'Left', detail: "All customers have been marked as having left this booking." });
        this.updategrid(data.bookingId, false);
      }
      else {
        this.messageService.add({ severity: 'error', summary: 'Not Updated', detail: 'An error occurred checking out this booking.' });
      }
    }, () => {
      this.messageService.add({ severity: 'error', summary: 'Not Updated', detail: 'An error occurred checking out this booking.' });
    });
  }

  //Only the one row changes, so the grid is not reloaded underneath the filter the person on
  //the counter has typed.
  private updategrid(bookingId: string, checkedin: boolean): void {
    let booking = this.ListBookings.find(b => b.bookingId === bookingId);

    if (booking != null) {
      booking.checkedin = checkedin;
    }
  }
}
